/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box } from "@mui/material";
import React from "react";
import { useNavigate } from "react-router";

import { Latest, Next } from "src/store/api/generatedApi";

import { useTenantTranslation } from "hooks/formatters";

import { TitledTabs } from "components/common/titledTabs";

import { BasketLoadQueue } from "./BasketLoadQueue";
import { LatestNextToggle } from "./LatestNextToggle";
import { ActiveSupervisorPlan, BackupSupervisorPlan } from "./SupervisorPlan";

type Props =
  | {
      deploymentType: "standard";
      planTimeframe: Latest | Next;
    }
  | {
      deploymentType: "backup";
    };

export const SupervisorView = ({ ...props }: Props) => {
  const navigate = useNavigate();

  const { t } = useTenantTranslation();

  const cachedPlantimeFrameRef = React.useRef(
    props.deploymentType === "standard" ? props.planTimeframe : undefined
  );

  const handleOnChange = React.useCallback(
    (key: "active" | "backup") => {
      switch (key) {
        case "active": {
          void navigate(
            `../standard/${cachedPlantimeFrameRef.current ?? "latest"}`
          );
          break;
        }
        case "backup": {
          void navigate("../backup");
          break;
        }
      }
    },
    [navigate]
  );

  if (props.deploymentType === "standard") {
    cachedPlantimeFrameRef.current = props.planTimeframe;
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 300px",
        gridTemplateRows: "min-content 1fr",
        minHeight: "100%",
      }}
    >
      <Box
        sx={{
          width: "100%",
          gridRow: "1 / span 2",
          overflow: "auto",
          borderRight: "1px solid black",
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
        }}
      >
        <TitledTabs
          selectedTabKey={
            props.deploymentType === "standard" ? "active" : "backup"
          }
          onChange={handleOnChange}
          tabsSx={{ px: 2 }}
          containerSx={{ px: 2 }}
          rightComponent={
            props.deploymentType === "standard" &&
            props.planTimeframe !== undefined ? (
              <LatestNextToggle value={props.planTimeframe} />
            ) : undefined
          }
          tabSpecs={[
            {
              title: t("active"),
              key: "active",
              content:
                props.deploymentType === "standard" ? (
                  <ActiveSupervisorPlan planTimeframe={props.planTimeframe} />
                ) : null,
            },
            {
              title: t("backup"),
              key: "backup",
              content: <BackupSupervisorPlan />,
            },
          ]}
        />
      </Box>
      <BasketLoadQueue />
    </Box>
  );
};
